import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter } from 'react-icons/fi';
import { clientAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

/**
 * Catégories CRM des clients — Modèle "Le propriétaire est un client"
 * Le mandat est signé entre l'agence et le client de catégorie 'owner'.
 */
const CLIENT_CATEGORIES = [
  { value: 'owner',    label: 'Propriétaire',  color: '#7c3aed' },
  { value: 'tenant',   label: 'Locataire',     color: '#0ea5e9' },
  { value: 'buyer',    label: 'Acheteur',      color: '#16a34a' },
  { value: 'seller',   label: 'Vendeur',       color: '#d97706' },
  { value: 'investor', label: 'Investisseur',  color: '#be185d' },
  { value: 'prospect', label: 'Prospect',      color: '#6b7280' },
  { value: 'vendor',   label: 'Prestataire',   color: '#64748b' },
];

const CLIENT_TYPES = [
  { value: 'individual', label: 'Particulier' },
  { value: 'company',    label: 'Entreprise'  },
];

const getCategoryStyle = (category) => {
  const cat = CLIENT_CATEGORIES.find(c => c.value === category);
  if (!cat) return {};
  return {
    background: cat.color + '22',
    color: cat.color,
    border: `1px solid ${cat.color}55`,
    borderRadius: 999,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block',
  };
};

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  city: '',
  client_type: 'individual',
  client_category: 'prospect',
  source: '',
  notes: '',
  create_account: false,
  password: '',
};

const ClientListPage = () => {
  const [clients, setClients]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [search, setSearch]         = useState('');
  const [filterCat, setFilterCat]   = useState('');
  const [form, setForm]             = useState(INITIAL_FORM);
  const navigate = useNavigate();

  const fetchClients = () => {
    setLoading(true);
    clientAPI
      .list({ search, client_category: filterCat || undefined })
      .then(r => { setClients(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchClients(); }, [search, filterCat]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (form.create_account) {
        await clientAPI.createWithAccount(form);
      } else {
        await clientAPI.create(form);
      }
      setShowModal(false);
      setForm(INITIAL_FORM);
      fetchClients();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la création du client');
    }
  };

  const columns = [
    {
      header: 'Nom',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>
          {r.first_name} {r.last_name}
        </span>
      ),
    },
    { header: 'Email',      accessor: 'email' },
    { header: 'Téléphone',  accessor: 'phone' },
    {
      header: 'Catégorie',
      render: (r) => (
        <span style={getCategoryStyle(r.client_category)}>
          {CLIENT_CATEGORIES.find(c => c.value === r.client_category)?.label || r.client_category || '—'}
        </span>
      ),
    },
    {
      header: 'Personne',
      render: (r) => (
        <span className="badge badge-info">
          {CLIENT_TYPES.find(t => t.value === r.client_type)?.label || r.client_type}
        </span>
      ),
    },
    { header: 'Leads',        accessor: 'lead_count' },
    { header: 'Interactions', accessor: 'interaction_count' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Clients</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <FiPlus /> Nouveau client
        </button>
      </div>

      {/* Barre de filtres */}
      <div className="filters" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 320 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-secondary)' }} />
          <input
            className="form-control"
            style={{ paddingLeft: 36 }}
            placeholder="Rechercher par nom, email, téléphone…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiFilter style={{ color: 'var(--text-secondary)' }} />
          <select
            className="form-control"
            style={{ minWidth: 160 }}
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
          >
            <option value="">Toutes catégories</option>
            {CLIENT_CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <div className="card">
          <DataTable
            columns={columns}
            data={clients}
            onRowClick={(r) => navigate(`/clients/${r.id}`)}
            emptyMessage="Aucun client trouvé"
          />
        </div>
      )}

      {/* Modal création client */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouveau client">
        <form onSubmit={handleCreate}>
          {/* Identité */}
          <div className="grid-2">
            <div className="form-group">
              <label>Prénom *</label>
              <input className="form-control" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Nom *</label>
              <input className="form-control" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
            </div>
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label>Email</label>
              <input type="email" className="form-control" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Téléphone</label>
              <input className="form-control" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <label>Ville</label>
            <input className="form-control" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
          </div>

          {/* Classification */}
          <div className="grid-2" style={{ marginTop: 4 }}>
            <div className="form-group">
              <label>
                Catégorie CRM
                <span
                  title="Rôle métier du client dans la relation avec l'agence. Le propriétaire signe le mandat avec l'agence."
                  style={{ marginLeft: 6, cursor: 'help', color: 'var(--text-secondary)', fontSize: 13 }}
                >ⓘ</span>
              </label>
              <select
                className="form-control"
                value={form.client_category}
                onChange={e => setForm({ ...form, client_category: e.target.value })}
              >
                {CLIENT_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Type de personne</label>
              <select
                className="form-control"
                value={form.client_type}
                onChange={e => setForm({ ...form, client_type: e.target.value })}
              >
                {CLIENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea className="form-control" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>

          {/* Option création compte portail */}
          <div className="form-group" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.create_account}
                onChange={e => setForm({ ...form, create_account: e.target.checked })}
              />
              <span>Créer un compte portail client (accès au dashboard)</span>
            </label>
          </div>

          {form.create_account && (
            <div className="form-group">
              <label>Mot de passe *</label>
              <input
                type="password"
                className="form-control"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                required={form.create_account}
                placeholder="Mot de passe pour le portail client"
              />
              <small style={{ color: 'var(--text-secondary)' }}>
                Le client pourra se connecter avec son email et ce mot de passe
              </small>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">
              {form.create_account ? 'Créer avec compte' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClientListPage;
